import {GridRowSelectionModel} from "@mui/x-data-grid";
import JSZip from "jszip";
import _, {filter, isEmpty, map} from "lodash";
import {gzip} from "pako";
import {FileRejection} from "react-dropzone";
import {BatchLevelMediaFile, DownloadLink, FileRead, FileType, PartnerFileRead} from "../declarations";

const downloadUrl = (url: string, filename?: string, openInNewTab?: boolean): void => {
  const link = document.createElement("a");
  link.href = url;
  if (openInNewTab) link.target = "_blank";
  if (filename) link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const downloadFilesAsZip = async ({files, accountPublicId}: {files: DownloadLink[]; accountPublicId?: string}) => {
  const zip = new JSZip();
  try {
    for (const file of files) {
      const response = await fetch(file.url);
      if (response.ok) {
        const fileBlob = await response.blob();
        zip.file(file.file_name, fileBlob);
      }
    }
    const zipBlob = await zip.generateAsync({type: "blob"});
    const link = document.createElement("a");
    link.href = URL.createObjectURL(zipBlob);
    if (accountPublicId) {
      link.download = `Docs-${accountPublicId}.zip`;
    } else {
      link.download = `Docs.zip`;
    }
    link.click();
    URL.revokeObjectURL(link.href);
  } catch (error) {
    console.error("Failed to download files as zip:", error);
  }
};
const getDownloadLinkFromRow = (selected: GridRowSelectionModel, mapIdToUrl: Record<string, DownloadLink>) => {
  return selected.map((fileId) => {
    const downloadLink = mapIdToUrl[String(fileId)]; // Use String for key lookup
    return downloadLink || {url: "", file_name: ""}; // Provide a fallback if not found
  });
};

interface IAppendUrlParams {
  excludeFiles?: FileType[];
  includeFiles?: FileType[];
  fileIds?: string[];
}

const appendUrlParams = ({excludeFiles, includeFiles, fileIds}: IAppendUrlParams) => {
  const hasIncludedFiles = includeFiles?.length;
  const hasExcludedFiles = excludeFiles?.length;
  let appendedQueryParams = "";
  if (hasIncludedFiles) {
    appendedQueryParams += getIncludeFileQueryParam(includeFiles);
  }
  if (hasExcludedFiles) {
    appendedQueryParams += getExcludeFileQueryParam(excludeFiles);
  }
  if (fileIds) {
    appendedQueryParams += getFileIdsQueryParam(fileIds);
  }
  return isEmpty(appendedQueryParams) ? "" : `?${appendedQueryParams}`;
};

const getExcludeFileQueryParam = (excludeFiles: FileType[]): string => {
  return excludeFiles.map((fileType) => `exclude_files=${fileType}`).join("&");
};

const getIncludeFileQueryParam = (includeFiles: FileType[]): string => {
  return includeFiles.map((fileType) => `include_files=${fileType}`).join("&");
};

const getFileIdsQueryParam = (fileIds: string[]): string => {
  return fileIds.map((fileId) => `file_ids=${fileId}`).join("&");
};

const getFilesRejectedMessageKey = (fileRejections?: FileRejection | FileRejection[]): string => {
  if (!fileRejections) {
    return "Cannot upload selected file.";
  }

  if (_.isArray(fileRejections) && _.isEmpty(fileRejections)) {
    return "Cannot upload selected file.";
  }

  const fileRejection: FileRejection = _.isArray(fileRejections) ? fileRejections[0] : fileRejections;
  const {errors} = fileRejection;

  if (errors?.length) {
    const error = errors[0];
    const {code} = error;

    switch (code) {
      case "file-too-large":
        return "Cannot upload selected file. File size too large.";
      case "file-too-small":
        return "Cannot upload selected file. File size too small.";
      case "too-many-files":
        return "Cannot upload selected file. Too many files selected.";
      case "file-invalid-type":
        return "Cannot upload selected file. Invalid file type.";
      default:
        return "Cannot upload selected file.";
    }
  }

  return "Cannot upload selected file.";
};

const isNotChromeOniPhone = () => {
  const userAgent = navigator.userAgent;
  const isiPhone = /iPhone/i.test(userAgent);
  const isChrome = /CriOS/i.test(userAgent);

  return isiPhone && !isChrome;
};

const getViewMode = (fileName?: string, viewMode?: boolean): boolean => {
  if (!viewMode || !fileName) {
    return false;
  }
  const isSafari = isNotChromeOniPhone();

  return (
    !isSafari &&
    ["pdf", "html", "png", "jpeg", "jpg", "svg", "svg+xml", "tif", "tiff", "mp3"].some((fileType) =>
      fileName?.endsWith(fileType)
    )
  );
};

const compressFile = async (file: File): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsArrayBuffer(file);
    reader.onload = () => {
      try {
        const compressed = gzip(new Uint8Array(reader.result as ArrayBuffer));
        const blobFile = new Blob([compressed], {type: "application/gzip"});
        resolve(blobFile);
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = () => reject(reader.error);
  });
};

const mapIdToUrl = (files: PartnerFileRead[] | BatchLevelMediaFile[] | FileRead[]) => {
  return Object.fromEntries(
    map(
      filter(files, (file: PartnerFileRead | BatchLevelMediaFile | FileRead) => file.id && file.url),
      (file: any) => [file.id, {url: file.url!, file_name: file.filename || ""}]
    )
  );
};

type downloadFileVariables = {
  accountId?: string;
  fileId: string;
  isMobile?: boolean;
  viewMode?: boolean;
  fileName?: string;
  htmlView?: boolean;
};

export {
  downloadUrl,
  downloadFilesAsZip,
  getDownloadLinkFromRow,
  appendUrlParams,
  getFilesRejectedMessageKey,
  getViewMode,
  compressFile,
  mapIdToUrl,
};
export type {downloadFileVariables};
